import { toOptionalConvexTableId } from "~/lib/convex-table-id";

import type { ItemFormData, ItemMutationData, Price } from "./item-form-types";

// Helper to convert form price to API price format
export const transformPrice = (
  price: Price
): number | { default: number; locations?: Record<string, number> } => {
  if (typeof price === "string") {
    return Number.parseFloat(price);
  }
  // It's an object with location prices
  const entries = Object.entries(price);
  const defaultEntry = entries.find(([key]) => key === "default");
  const locationEntries = entries.filter(([key]) => key !== "default");

  if (defaultEntry ?? locationEntries.length === 0) {
    return Number.parseFloat(defaultEntry?.[1] ?? "0");
  }

  return {
    default: Number.parseFloat(entries[0]?.[1] ?? "0"),
    locations: Object.fromEntries(
      locationEntries.map(([locationId, value]) => [
        locationId,
        Number.parseFloat(value),
      ])
    ),
  };
};

// Helper to transform form data into mutation payload
export const buildMutationData = (data: ItemFormData): ItemMutationData => {
  const { devicesIds: _, onlinePrice, ...rest } = data;
  const parsedOnlinePrice = onlinePrice
    ? Number.parseFloat(onlinePrice)
    : undefined;
  // "0" is the sentinel id of the synthetic "default" tax category.
  const taxCategoryId =
    data.taxCategoryId === "0" ? undefined : (data.taxCategoryId ?? undefined);
  return {
    ...rest,
    price: transformPrice(data.price),
    imageUrl: data.imageUrl ?? undefined,
    taxCategoryId: toOptionalConvexTableId("taxCategories", taxCategoryId),
    salesCategoryId: data.salesCategoryId ?? undefined,
    onlinePrice:
      parsedOnlinePrice && !Number.isNaN(parsedOnlinePrice)
        ? parsedOnlinePrice
        : undefined,
  };
};
